import type { StatusResponse } from "../shared/types";

const WIDTH = 1080;
const HEIGHT = 1920;
const RED = "#DB1200";
const PINK = "#FF52EC";
const BLACK = "#000000";
const FONT_BODY = '"Helvetica Neue LT Pro 75 Bold"';
const FONT_DISPLAY = '"Helvetica Neue LT Pro 87 Heavy Condensed"';
const TEXT_ONLY_NAME_HEIGHT = 104;

export const SOCIAL_PORTRAIT_FILTER = "grayscale(1) brightness(1.13) contrast(1.2) saturate(0)";

export const centeredTextOnlyStoryTop = (messagePanelHeight: number): number =>
  Math.round((HEIGHT - TEXT_ONLY_NAME_HEIGHT - messagePanelHeight) / 2);

export const socialCaption = (hashtags: string): string =>
  `${hashtags}\n\n@madonna @madonnahq @outernetlondon @alexwolf.live @outernetglobal`;

const loadImage = async (blob: Blob): Promise<HTMLImageElement> => {
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
};

const drawCover = (
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
) => {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const sourceWidth = width / scale;
  const sourceHeight = height / scale;
  const sourceX = (image.naturalWidth - sourceWidth) / 2;
  const sourceY = (image.naturalHeight - sourceHeight) / 2;
  context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
};

const linesFor = (context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const words = text.trim().split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (line && context.measureText(next).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
};

const fitText = (
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
  sizes: number[],
) => {
  for (const size of sizes) {
    context.font = `700 ${size}px ${FONT_BODY}`;
    const lines = linesFor(context, text, maxWidth);
    if (lines.length <= maxLines) return { lines, size, lineHeight: Math.round(size * 1.08) };
  }
  const size = sizes.at(-1) ?? 34;
  context.font = `700 ${size}px ${FONT_BODY}`;
  const lines = linesFor(context, text, maxWidth).slice(0, maxLines);
  if (lines.length === maxLines) {
    const last = lines.length - 1;
    while (lines[last].length > 1 && context.measureText(`${lines[last]}…`).width > maxWidth) {
      lines[last] = lines[last].slice(0, -1).trimEnd();
    }
    lines[last] = `${lines[last]}…`;
  }
  return { lines, size, lineHeight: Math.round(size * 1.08) };
};

const drawFittedLine = (
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  sizes: number[],
  fontFamily = FONT_DISPLAY,
) => {
  for (const size of sizes) {
    context.font = `900 ${size}px ${fontFamily}`;
    if (context.measureText(text).width <= maxWidth) {
      context.fillText(text, x, y);
      return;
    }
  }
  context.font = `900 ${sizes.at(-1) ?? 34}px ${fontFamily}`;
  context.fillText(text, x, y, maxWidth);
};

const drawCenteredFittedLine = (
  context: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  y: number,
  maxWidth: number,
  sizes: number[],
) => {
  context.save();
  context.textAlign = "center";
  drawFittedLine(context, text, centerX, y, maxWidth, sizes);
  context.restore();
};

const drawFooter = (context: CanvasRenderingContext2D, hashtags: string) => {
  context.fillStyle = BLACK;
  context.font = `700 25px ${FONT_BODY}`;
  const hashtagLines = linesFor(context, hashtags, 972).slice(0, 2);
  hashtagLines.forEach((line, index) => context.fillText(line, 54, 1834 + index * 31));
};

const roundedRectPath = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const corner = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + corner, y);
  context.lineTo(x + width - corner, y);
  context.quadraticCurveTo(x + width, y, x + width, y + corner);
  context.lineTo(x + width, y + height - corner);
  context.quadraticCurveTo(x + width, y + height, x + width - corner, y + height);
  context.lineTo(x + corner, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - corner);
  context.lineTo(x, y + corner);
  context.quadraticCurveTo(x, y, x + corner, y);
  context.closePath();
};

const drawLocationPin = (context: CanvasRenderingContext2D, x: number, y: number) => {
  context.beginPath();
  context.moveTo(x, y + 38);
  context.bezierCurveTo(x - 5, y + 30, x - 17, y + 20, x - 17, y + 8);
  context.bezierCurveTo(x - 17, y - 3, x - 9, y - 11, x, y - 11);
  context.bezierCurveTo(x + 9, y - 11, x + 17, y - 3, x + 17, y + 8);
  context.bezierCurveTo(x + 17, y + 20, x + 5, y + 30, x, y + 38);
  context.closePath();
  context.fill();
  context.fillStyle = BLACK;
  context.beginPath();
  context.arc(x, y + 7, 5, 0, Math.PI * 2);
  context.fill();
};

const drawLocation = (context: CanvasRenderingContext2D) => {
  const x = 54;
  const y = 1738;
  const width = 424;
  const height = 72;

  roundedRectPath(context, x, y, width, height, 13);
  context.fillStyle = BLACK;
  context.fill();
  context.lineWidth = 4;
  context.strokeStyle = PINK;
  context.stroke();

  context.fillStyle = PINK;
  drawLocationPin(context, x + 42, y + 23);
  context.fillStyle = "#FFFFFF";
  context.font = `700 28px ${FONT_BODY}`;
  context.fillText("OUTERNET LONDON", x + 78, y + 20);
};

export const buildInstagramPost = async (status: StatusResponse): Promise<Blob> => {
  if (!status.name || !status.message) throw new Error("Post details are not ready.");
  await Promise.all([
    document.fonts.load('700 48px "Helvetica Neue LT Pro 75 Bold"'),
    document.fonts.load('900 48px "Helvetica Neue LT Pro 87 Heavy Condensed"'),
    document.fonts.ready,
  ]);
  let portrait: HTMLImageElement | null = null;
  if (status.imageUrl) {
    const portraitResponse = await fetch(status.imageUrl, { cache: "no-store" });
    if (!portraitResponse.ok) throw new Error("Portrait could not be loaded.");
    portrait = await loadImage(await portraitResponse.blob());
  }

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Poster canvas is unavailable.");

  context.textBaseline = "top";
  context.fillStyle = RED;
  context.fillRect(0, 0, WIDTH, HEIGHT);

  context.fillStyle = BLACK;
  drawFittedLine(context, "MADONNA CELEBRATION", 54, 48, 972, [72, 66, 60]);

  const name = status.name.toUpperCase();
  const message = fitText(context, status.message, portrait ? 780 : 808, portrait ? 4 : 8, portrait
    ? [46, 42, 38, 34, 30]
    : [68, 60, 54, 48, 42, 36]);

  if (portrait) {
    // Instagram Story (9:16) containing the same editorial unit as the walls:
    // a true portrait photo, a name tab crossing its lower edge, then the message.
    context.save();
    context.filter = SOCIAL_PORTRAIT_FILTER;
    drawCover(context, portrait, 90, 170, 900, 1200);
    context.restore();
    if (status.photoDisplayState === "temporary") {
      const flash = context.createRadialGradient(522, 550, 20, 522, 550, 520);
      flash.addColorStop(0, "rgba(255,255,255,.18)");
      flash.addColorStop(1, "rgba(255,255,255,0)");
      context.fillStyle = flash;
      context.fillRect(90, 170, 900, 1200);
    }

    const nameTabWidth = 560;
    const nameTabX = (WIDTH - nameTabWidth) / 2;
    const nameTabY = 1248;
    context.fillStyle = PINK;
    context.fillRect(nameTabX, nameTabY, nameTabWidth, 92);
    context.fillStyle = BLACK;
    drawCenteredFittedLine(context, name, WIDTH / 2, nameTabY + 14, 500, [58, 54, 50, 46, 42]);

    const messagePanelY = 1336;
    context.fillStyle = BLACK;
    context.fillRect(108, messagePanelY, 864, 320);
    context.fillStyle = PINK;
    context.font = `700 ${message.size}px ${FONT_BODY}`;
    const messageHeight = message.lines.length * message.lineHeight;
    const messageY = messagePanelY + Math.max(34, (320 - messageHeight) / 2);
    message.lines.forEach((line, index) => context.fillText(line, 150, messageY + index * message.lineHeight));
  } else {
    const nameTabWidth = 720;
    const nameTabX = (WIDTH - nameTabWidth) / 2;
    context.font = `700 ${message.size}px ${FONT_BODY}`;
    const messageHeight = message.lines.length * message.lineHeight;
    const messagePanelWidth = Math.min(
      916,
      Math.max(300, Math.ceil(Math.max(...message.lines.map((line) => context.measureText(line).width))) + 108),
    );
    const messagePanelHeight = messageHeight + 100;
    const storyTop = centeredTextOnlyStoryTop(messagePanelHeight);

    context.fillStyle = PINK;
    context.fillRect(nameTabX, storyTop, nameTabWidth, TEXT_ONLY_NAME_HEIGHT);
    context.fillStyle = BLACK;
    drawCenteredFittedLine(context, name, WIDTH / 2, storyTop + 16, 660, [62, 58, 54, 48]);

    context.font = `700 ${message.size}px ${FONT_BODY}`;
    const messagePanelX = (WIDTH - messagePanelWidth) / 2;
    const messagePanelY = storyTop + TEXT_ONLY_NAME_HEIGHT;
    context.fillStyle = BLACK;
    context.fillRect(messagePanelX, messagePanelY, messagePanelWidth, messagePanelHeight);
    context.fillStyle = PINK;
    const messageX = messagePanelX + 54;
    const messageY = messagePanelY + 50;
    message.lines.forEach((line, index) => context.fillText(line, messageX, messageY + index * message.lineHeight));
  }

  drawLocation(context);
  drawFooter(context, status.hashtags);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Poster could not be exported.")), "image/jpeg", 0.94);
  });
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
};
